import { formatVersionBarAmount } from "@components/PayRangeVersionBar/payRangeVersionBarUtils";
import {
  EFFECTIVE_STATE_LABELS,
  OPEN_ENDED_DASH,
  parseLocalDate,
} from "@components/utils/effectiveDated";
import { formatFairPayMonthYear, formatFairPayVersion } from "@components/utils/fairPayFormat";

/**
 * Pure helpers for PayRangeVersionRow (ER-11222).
 *
 * All date comparisons happen at the day level (local calendar day), so
 * time-of-day and timezone never affect the Lapsed / Current / Future boundary —
 * a version stays Current for the entirety of its effectiveFrom and effectiveTo
 * days.
 */

export const PAY_RANGE_VERSION_STATUS = {
  LAPSED: "lapsed",
  CURRENT: "current",
  FUTURE: "future",
};

const EN_DASH = "\u2013";
const MIDDLE_DOT = "\u00B7";

/** Returns a Date at local midnight for the value's calendar day, or null. */
const normalizeDay = parseLocalDate;

/**
 * Resolve a version's Lapsed / Current / Future state purely from its effective
 * dates versus `today`. Never set manually.
 *
 * - Future — today < effectiveFrom (version hasn't started yet).
 * - Lapsed — today > effectiveTo (version already ended).
 * - Current — effectiveFrom <= today <= effectiveTo, a missing bound being
 *   open-ended. A version with no effective dates at all is open on both sides,
 *   so it is Current (Effective-Dated Records §4).
 */
export function resolvePayRangeVersionStatus(effectiveFrom, effectiveTo, today) {
  const from = normalizeDay(effectiveFrom);
  const to = normalizeDay(effectiveTo);
  const todayDate = normalizeDay(today == null ? new Date() : today);

  if (from != null && todayDate < from) return PAY_RANGE_VERSION_STATUS.FUTURE;
  if (to != null && todayDate > to) return PAY_RANGE_VERSION_STATUS.LAPSED;
  return PAY_RANGE_VERSION_STATUS.CURRENT;
}

/** "Feb 2026" for a date value, or "" when the value is missing/invalid. */
export function formatPayRangeVersionDate(value) {
  return formatFairPayMonthYear(value);
}

/** True when the version has a start but no (valid) end date: shown as "—", tooltip "Open-ended". */
export function isPayRangeVersionOpenEnded(effectiveFrom, effectiveTo) {
  return !!formatPayRangeVersionDate(effectiveFrom) && !formatPayRangeVersionDate(effectiveTo);
}

/**
 * "V{version} · {from month/year} –" — the end ("{to month/year}" or the open-ended "—")
 * follows via formatPayRangeVersionEnd. Falls back to "V{version}" when no date can be shown.
 */
export function formatPayRangeVersionLead(version, effectiveFrom) {
  const from = formatPayRangeVersionDate(effectiveFrom);
  const versionLabel = formatFairPayVersion(version);
  if (!from) return versionLabel;
  return `${versionLabel} ${MIDDLE_DOT} ${from} ${EN_DASH}`;
}

/** "Dec 2026", or "—" for an open-ended version. */
export function formatPayRangeVersionEnd(effectiveTo) {
  return formatPayRangeVersionDate(effectiveTo) || OPEN_ENDED_DASH;
}

/**
 * "V{version} · {from month/year} – {to month/year}" with "—" for an open-ended
 * version. Falls back to "V{version}" when no date can be shown.
 */
export function formatPayRangeVersionLabel(version, effectiveFrom, effectiveTo) {
  const lead = formatPayRangeVersionLead(version, effectiveFrom);
  if (!formatPayRangeVersionDate(effectiveFrom)) return lead;
  return `${lead} ${formatPayRangeVersionEnd(effectiveTo)}`;
}

/**
 * Compact pay range, e.g. "$108K–$145K". Blank when both min and max are unset;
 * a single bound renders on its own.
 */
export function formatPayRangeVersionAmount(minAmount, maxAmount) {
  const min = formatVersionBarAmount(minAmount);
  const max = formatVersionBarAmount(maxAmount);
  if (!min && !max) return "";
  if (!min) return max;
  if (!max) return min;
  return `${min}${EN_DASH}${max}`;
}

/** Accessible row label that includes the status, not just the background color. */
export function formatPayRangeVersionAriaLabel(
  status,
  version,
  effectiveFrom,
  effectiveTo,
  minAmount,
  maxAmount
) {
  const key =
    PAY_RANGE_VERSION_STATUS[String(status || "").toUpperCase()] || PAY_RANGE_VERSION_STATUS.LAPSED;
  const state = EFFECTIVE_STATE_LABELS[key];
  const from = formatPayRangeVersionDate(effectiveFrom) || "unknown";
  const to = formatPayRangeVersionDate(effectiveTo) || "open-ended";
  const min = formatVersionBarAmount(minAmount);
  const max = formatVersionBarAmount(maxAmount);
  const range = min && max ? `, ${min} to ${max}` : "";
  return `${state} version ${version}, ${from} to ${to}${range}`;
}
